<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
    title="KCPS VNコネクト一覧"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <!-- BODY -->
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container fluid>
            <!-- 回線情報 -->
            <div>
              <h3>回線情報</h3>
            </div>
            <v-row class="my-1 ml-3">
              <v-col cols="4">
                <app-label-group
                  label="回線番号"
                  :value="vnConnectList.enumber"
                />
              </v-col>
              <v-col cols="4">
                <app-label-group
                  label="アクセス回線メニュー"
                  :value="
                    $filter.enumTo(
                      vnConnectList.accessType,
                      'cloudVn_accessType'
                    ) +
                    '(' +
                    $filter.bandwidthTo(accessLineMenuBandwidth, 'Mbps') +
                    ')'
                  "
                />
              </v-col>
              <v-col cols="4">
                <app-label-group
                  label="事業所名"
                  :value="vnConnectList.office"
                />
              </v-col>
            </v-row>

            <!-- クラウド情報 -->
            <h3>クラウド情報</h3>
            <v-row class="mt-1 ml-3 mb-3">
              <v-col cols="4">
                <app-label-group label="種別" value="KCPS" />
              </v-col>
              <v-col cols="4">
                <app-label-group label="帯域">
                  <template #value>
                    <span class="ml-3"
                      >{{
                        $filter.bandwidthTo(vnConnectList.bandwidth, "Mbps")
                      }}(設定可能帯域:</span
                    >
                    <span
                      :class="{
                        'text-error': remainingBandwidth.value < 0,
                      }"
                      >{{
                        $filter.bandwidthTo(remainingBandwidth, "Mbps")
                      }}</span
                    >)
                  </template>
                </app-label-group>
              </v-col>
              <v-col>
                <app-label-group label="無料枠利用">
                  <template #value>
                    <span class="ml-3">{{
                      $filter.enumTo(billingFreeUsage, "billingFreeUsage")
                    }}</span>
                  </template>
                </app-label-group>
              </v-col>
            </v-row>

            <!-- VNコネクト情報 -->
            <div class="d-flex justify-space-between mb-4">
              <div class="d-flex align-end">
                <h3>VNコネクト情報</h3>
              </div>
              <div class="d-flex ga-2">
                <v-btn
                  v-if="billingFreeOptionBtn"
                  color="info"
                  @click="billingFreeOption"
                  v-permission.config
                  >無料枠選択</v-btn
                >
                <v-btn color="primary" @click="register" v-permission.config
                  >追加</v-btn
                >
                <v-btn
                  color="primary"
                  @click="modifyStatus(true)"
                  v-permission.config
                  >有効</v-btn
                >
                <v-btn
                  color="primary"
                  @click="modifyStatus(false)"
                  v-permission.config
                  >無効</v-btn
                >
              </div>
            </div>
            <v-alert
              v-if="alertMsg"
              class="my-2 text-left"
              :text="alertMsg"
            ></v-alert>
            <div v-permission.config class="text-left">
              <v-alert v-if="retry" class="my-2 bg-error"
                >{{
                  $filter.message("vn_connect_has_wnumber", {
                    wnumber: wnumberSelected,
                  })
                }}
                <a
                  class="text-primary text-decoration-underline cursor-pointer"
                  @click="retryVnConnect()"
                  >再実行はこちら</a
                >
              </v-alert>
              <v-alert v-if="pendingDelete" class="my-2 bg-error"
                >{{
                  $filter.message("vn_connect_has_pending_delete", {
                    wnumber: wnumberSelectedPendingDelete,
                  })
                }}
                <a
                  class="text-primary text-decoration-underline cursor-pointer"
                  @click="pendingDeleteVnConnect()"
                  >削除はこちら</a
                >
              </v-alert>
              <v-alert v-if="!retry && !pendingDelete && error" class="my-2">
                <span>{{ $filter.message("vn_connect_has_error") }}</span>
                <template
                  v-for="(wnumber, index) in wnumberSelectedError"
                  :key="wnumber"
                >
                  <a
                    class="text-primary text-decoration-underline cursor-pointer"
                    @click="selectTable(wnumber)"
                  >
                    {{ wnumber }}
                  </a>
                  <span
                    v-if="index < wnumberSelectedError.length - 1"
                    class="text-primary"
                  >
                    ,
                  </span>
                </template>
              </v-alert>
            </div>

            <app-table
              primary-key="cloudVnConnectAct.wnumber"
              :fields="vnConnects.fields"
              :items="vnConnectList.cloudVnConnectsList"
              :selectable="selectable"
              v-model="vnConnects.selected"
              :hover="true"
              v-model:table-sort-by="vnConnects.sortBy"
              @click:row="showConnectionDetails"
              :td-class="cellClass"
            >
              <template #cell(wnumber)="{ item }">
                {{ item.cloudVnConnectAct.wnumber }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  item.cloudVnConnectSby.wnumber
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(vnConnectName)="{ item }">
                {{ item.cloudVnConnectAct.vnConnectName }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  item.cloudVnConnectSby.vnConnectName
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(isVnConnectStatus)="{ item }">
                {{
                  $filter.enumTo(
                    item.cloudVnConnectAct.isVnConnectStatus,
                    "enable"
                  )
                }}<br />
                <template v-if="item.cloudVnConnectSby">{{
                  $filter.enumTo(
                    item.cloudVnConnectSby.isVnConnectStatus,
                    "enable"
                  )
                }}</template>
                <template v-else>-</template>
              </template>
              <template #cell(bandwidth)="{ item }">
                {{ item.cloudVnConnectCommon.bandwidthType }}({{
                  $filter.bandwidthTo(item.cloudVnConnectCommon.bandwidth)
                }})
              </template>
              <template #cell(routingType)="{ item }">
                {{ item.cloudVnConnectCommon.routingType }}
              </template>
              <template #cell(billingFree)="{ item }">
                {{
                  $filter.enumTo(
                    item.cloudVnConnectCommon.billingFree,
                    "hasBillingFree"
                  )
                }}
              </template>
              <template #cell(version)="{ item }">
                {{
                  $filter.enumTo(
                    item.cloudVnConnectCommon.version,
                    "kcpsVersion"
                  )
                }}
              </template>
              <template #cell(provStatus)="{ item }">
                <span
                  :class="statusColor(item.cloudVnConnectCommon.provStatus)"
                  style="white-space: pre-wrap"
                  class="d-flex align-center"
                >
                  {{
                    $filter.enumTo(
                      item.cloudVnConnectCommon.provStatus,
                      "kcpsStatus"
                    )
                  }}
                </span>
              </template>
              <template #cell(description)="{ item }">
                {{
                  $filter.emptyTo(item.cloudVnConnectCommon.description, "-")
                }}
              </template>
            </app-table>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <!-- FOOTER -->
      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { CloudVnKcpsList } from "@/apis/CloudVnKcpsApi";
import type { AppTableHeader, SortItem } from "@/components/AppTable.vue";
import MultiCloudKcps3UtilitySelect from "@/modals/multicloud/kcps3/MultiCloudKcps3UtilitySelect.vue";
import V1MultiCloudKcps3VnConnectionReference from "@/modals/multicloud/kcps3/v1/V1MultiCloudKcps3VnConnectionReference.vue";
import V2MultiCloudKcps3VnConnectionReference from "@/modals/multicloud/kcps3/v2/V2MultiCloudKcps3VnConnectionReference.vue";
import MultiCloudVNConnectStatusModify from "@/modals/multicloud/MultiCloudVNConnectStatusModify.vue";
import MultiCloudKcps3BillingFreeSelect from "@/modals/multicloud/kcps3/MultiCloudKcps3BillingFreeSelect.vue";
import MultiCloudKcps3VnConnectionRetry from "@/modals/multicloud/kcps3/MultiCloudKcps3VnConnectionRetry.vue";
import MultiCloudKcps3VnConnectionPendingDelete from "@/modals/multicloud/kcps3/MultiCloudKcps3VnConnectionPendingDelete.vue";
import type { Line, CloudLine } from "@/apis/CloudLineApi";

type CloudVnKcpsItem = CloudVnKcpsList["cloudVnConnectsList"][0];

export default defineComponent({
  name: "MultiCloudKcps3VnConnectionList",
  props: {
    /** クラウド回線SEQ */
    cloudLineSeq: {
      type: String as PropType<string>,
      required: true,
    },
    /** アクセス回線メニューの帯域 */
    accessLineMenuBandwidth: {
      type: Object as PropType<Line["bandwidth"]>,
      required: true,
    },
  },
  data() {
    return {
      vnConnectList: {} as CloudVnKcpsList,
      vnConnects: {
        sortBy: { key: "wnumber", order: "desc" } as SortItem,
        fields: [
          {
            key: "wnumber",
            title: "VNコネクト番号(Act/Sby)",
            width: "150px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnKcpsItem) => {
              return item.cloudVnConnectAct.wnumber;
            },
          },
          {
            key: "vnConnectName",
            title: "VNコネクト名(Act/Sby)",
            width: "140px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnKcpsItem) => {
              return item.cloudVnConnectAct.vnConnectName;
            },
          },
          {
            key: "isVnConnectStatus",
            title: "有効/無効(Act/Sby)",
            width: "110px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnKcpsItem) => {
              return this.$filter.enumTo(
                item.cloudVnConnectAct.isVnConnectStatus,
                "enable"
              );
            },
          },
          {
            key: "serviceKey",
            title: "KCPSユーザーID",
            width: "160px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnKcpsItem) => {
              return item.cloudVnConnectCommon.serviceKey;
            },
          },
          {
            key: "vpnVnCode",
            title: "接続先VPN/VNコード",
            width: "215px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnKcpsItem) => {
              if (item.cloudVnConnectCommon.vpnVnCode.startsWith("D")) {
                return `${item.cloudVnConnectCommon.vpnVnCode}(
                  ${item.cloudVnConnectCommon.vnType}
                )`;
              }
              return item.cloudVnConnectCommon.vpnVnCode;
            },
          },
          {
            key: "bandwidth",
            title: "品目",
            width: "160px",
            sortable: true,
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnKcpsItem) => {
              return this.$filter.bandwidthToBps(
                item.cloudVnConnectCommon.bandwidth
              );
            },
          },
          { key: "routingType", title: "ルーティング種別", width: "155px" },
          {
            key: "billingFree",
            title: "無料枠",
            width: "140px",
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnKcpsItem) => {
              return item.cloudVnConnectCommon.billingFree;
            },
          },
          {
            key: "version",
            title: "バージョン",
            width: "120px",
            sortByFormatted: true,
            formatter: (_v, _k, item: CloudVnKcpsItem) => {
              return item.cloudVnConnectCommon.version;
            },
          },
          {
            key: "provStatus",
            title: "ステータス",
            width: "140px",
            formatter: (_v, _k, item: CloudVnKcpsItem) => {
              return this.$filter.enumTo(
                item.cloudVnConnectCommon.provStatus,
                "kcpsStatus"
              );
            },
          },
          { key: "description", title: "備考", width: "180px" },
        ] as AppTableHeader<CloudVnKcpsList>[],
        selected: [] as CloudVnKcpsList["cloudVnConnectsList"],
      },
      alertMsg: null as string | null,
      isLoaded: false,
      cloudLineMaxBandwidth: {} as CloudLine["bandwidth"],
    };
  },
  computed: {
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
    /** 選択済みのW番号一覧. SbyがnullのW番号は一覧に含まれない */
    selectedWNumbers(): string[] {
      return this.vnConnects.selected.flatMap((e) => {
        return e.cloudVnConnectSby
          ? [e.cloudVnConnectAct.wnumber, e.cloudVnConnectSby.wnumber]
          : [e.cloudVnConnectAct.wnumber];
      });
    },
    /** 残りの設定可能帯域 */
    remainingBandwidth(): { value: number; unit: "MBPS" | "GBPS" } {
      const limitBandwidthMbps =
        (this.$filter.bandwidthToBps(this.vnConnectList.bandwidth) ?? 0) /
        1000 /
        1000;
      const sumBandwidthMbps = this.vnConnectList.cloudVnConnectsList.reduce(
        (a, e) => {
          const bandwidth = this.$filter.bandwidthToBps(
            e.cloudVnConnectCommon.bandwidth
          );
          if (e.cloudVnConnectCommon.billingFree && bandwidth! <= 1000000000) {
            return a;
          }
          return (
            a +
            (this.$filter.bandwidthToBps(e.cloudVnConnectCommon.bandwidth) ??
              0) /
              1000 /
              1000
          );
        },
        0
      );
      const diffBandwidthMbps = limitBandwidthMbps - sumBandwidthMbps;
      if (diffBandwidthMbps >= 1000 || diffBandwidthMbps <= -1000) {
        return { value: diffBandwidthMbps / 1000, unit: "GBPS" };
      } else {
        return { value: diffBandwidthMbps, unit: "MBPS" };
      }
    },
    /** 無料枠利用*/
    billingFreeUsage(): boolean {
      return this.vnConnectList.cloudVnConnectsList.some(
        (VnConnect) => VnConnect.cloudVnConnectCommon.billingFree
      );
    },
    /** 無料枠選択*/
    billingFreeOptionBtn(): boolean {
      return this.vnConnectList.cloudVnConnectsList.length > 0;
    },
    /** 再実行が必要なVNコネクトが存在する（V2のみ対象） */
    retry(): boolean {
      return (
        this.vnConnectList.cloudVnConnectsList.some(
          (item) =>
            item.cloudVnConnectCommon.version === "V2" &&
            (item.cloudVnConnectCommon.provStatus === "RETRYABLE" ||
              item.cloudVnConnectCommon.provStatus === "DELETE_RETRYABLE" ||
              item.cloudVnConnectCommon.provStatus === "CREATE_FAILED")
        ) ?? false
      );
    },

    /** 削除が必要なVNコネクトが存在する（V2のみ対象） */
    pendingDelete(): boolean {
      return (
        this.vnConnectList.cloudVnConnectsList.some(
          (item) =>
            item.cloudVnConnectCommon.version === "V2" &&
            item.cloudVnConnectCommon.provStatus === "PENDING_DELETE"
        ) ?? false
      );
    },

    /** エラー状態（V2のみ対象） */
    error(): boolean {
      return (
        this.vnConnectList.cloudVnConnectsList.some(
          (item) =>
            item.cloudVnConnectCommon.version === "V2" &&
            item.cloudVnConnectCommon.provStatus === "FAILED"
        ) ?? false
      );
    },
    /** 再実行対象のW番号（RETRYABLE/DELETE_RETRYABLE/CREATE_FAILED の最初の1件・V2のみ対象） */
    wnumberSelected(): string {
      const item = this.vnConnectList.cloudVnConnectsList.find(
        (item) =>
          item.cloudVnConnectCommon.version === "V2" &&
          (item.cloudVnConnectCommon.provStatus === "RETRYABLE" ||
            item.cloudVnConnectCommon.provStatus === "DELETE_RETRYABLE" ||
            item.cloudVnConnectCommon.provStatus === "CREATE_FAILED")
      );
      return item ? item.cloudVnConnectAct.wnumber : "";
    },
    /** エラー状態のW番号一覧（V2のみ対象） */
    wnumberSelectedError(): string[] {
      return this.vnConnectList.cloudVnConnectsList
        .filter(
          (item) =>
            item.cloudVnConnectCommon.version === "V2" &&
            item.cloudVnConnectCommon.provStatus === "FAILED"
        )
        .map((item) => item.cloudVnConnectAct.wnumber);
    },
    /** 削除対象のW番号（仕様上1件のみ・V2のみ対象） */
    wnumberSelectedPendingDelete(): string {
      const item = this.vnConnectList.cloudVnConnectsList.find(
        (item) =>
          item.cloudVnConnectCommon.version === "V2" &&
          item.cloudVnConnectCommon.provStatus === "PENDING_DELETE"
      );
      return item ? item.cloudVnConnectAct.wnumber : "";
    },
  },
  async mounted() {
    await this.load();
    if (this.retry && this.$service.permission.hasAuthority("CONFIG")) {
      this.retryVnConnect();
    } else if (
      this.pendingDelete &&
      this.$service.permission.hasAuthority("CONFIG")
    ) {
      this.pendingDeleteVnConnect();
    }
    this.isLoaded = true;
  },
  methods: {
    /** 読み込み */
    async load() {
      this.vnConnectList = await this.$api.cloudVnKcps.getVnConnects(
        this.cloudLineSeq
      );

      // 回線情報に紐づくクラウド回線情報を取得APIを呼び出す
      const lines = (await this.$api.cloudLine.getCloudLines()).lineList;
      const cloudLines = lines.flatMap(
        (v: { cloudLineList: any }) => v.cloudLineList
      );
      // クラウド回線の帯域最大値(品目の上限値制御用)
      this.cloudLineMaxBandwidth = cloudLines.find(
        (v: { cloudLineSeq: string }) => v.cloudLineSeq === this.cloudLineSeq
      )!.bandwidth;
    },
    /** VNコネクト追加 */
    async register() {
      this.alertMsg = null;
      await this.$modal.show(MultiCloudKcps3UtilitySelect, {
        cloudLineSeq: this.cloudLineSeq,
        cloudVnKcpsKeyId: this.vnConnectList.cloudVnKcpsKeyId,
        billingFreeUsage: this.billingFreeUsage,
        cloudLineMaxBandwidth: this.cloudLineMaxBandwidth,
        remainingBandwidth: this.$filter.bandwidthToBps(
          this.remainingBandwidth
        )!,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** 有効/無効 */
    async modifyStatus(isEnabled: boolean) {
      if (this.vnConnects.selected.length === 0) {
        this.alertMsg = this.$filter.message("no_select_data");
        return;
      } else {
        this.alertMsg = null;
      }

      const wNumbers: string[] = await this.$modal.show(
        MultiCloudVNConnectStatusModify,
        {
          enable: isEnabled,
          vnConnects: this.vnConnects.selected,
        }
      );

      if (isEnabled) {
        await this.$api.cloudVnKcps.putEnableVnConnect({
          cloudVnConnectList: wNumbers,
          cloudVnKcpsKeyId: this.vnConnectList.cloudVnKcpsKeyId,
        });
      } else {
        await this.$api.cloudVnKcps.putDisableVnConnect({
          cloudVnConnectList: wNumbers,
          cloudVnKcpsKeyId: this.vnConnectList.cloudVnKcpsKeyId,
        });
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** 無料枠選択 */
    async billingFreeOption() {
      this.alertMsg = null;
      await this.$modal.show(MultiCloudKcps3BillingFreeSelect, {
        cloudLineSeq: this.cloudLineSeq,
        vnConnectList: this.vnConnectList,
        cloudVnKcpsKeyId: this.vnConnectList.cloudVnKcpsKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** VNコネクト詳細表示 */
    async showConnectionDetails(entity: CloudVnKcpsItem) {
      this.alertMsg = null;
      const ReferenceComponent =
        entity.cloudVnConnectCommon.version === "V2"
          ? V2MultiCloudKcps3VnConnectionReference
          : V1MultiCloudKcps3VnConnectionReference;
      await this.$modal.show(ReferenceComponent, {
        wnumber: entity.cloudVnConnectAct.wnumber,
        cloudLineSeq: this.cloudLineSeq,
        billingFreeFlg: entity.cloudVnConnectCommon.billingFree,
        cloudLineMaxBandwidth: this.cloudLineMaxBandwidth,
        remainingBandwidth: this.$filter.bandwidthToBps(
          this.remainingBandwidth
        )!,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** ステータスカラー */
    statusColor(status: string) {
      if (
        status === "RETRYABLE" ||
        status === "DELETE_RETRYABLE" ||
        status === "FAILED" ||
        status === "CREATE_FAILED" ||
        status === "PENDING_DELETE"
      ) {
        return "text-error";
      }
    },
    /** VNコネクト再実行 */
    async retryVnConnect() {
      await this.$modal.show(MultiCloudKcps3VnConnectionRetry, {
        cloudLineSeq: this.cloudLineSeq,
        vnConnectList: this.vnConnectList,
        cloudVnKcpsKeyId: this.vnConnectList.cloudVnKcpsKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** VNコネクト削除 */
    async pendingDeleteVnConnect() {
      await this.$modal.show(MultiCloudKcps3VnConnectionPendingDelete, {
        cloudLineSeq: this.cloudLineSeq,
        vnConnectList: this.vnConnectList,
        cloudVnKcpsKeyId: this.vnConnectList.cloudVnKcpsKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** エラー行のVNコネクト情報を表示 */
    async selectTable(wnumber: string) {
      const entity = this.vnConnectList.cloudVnConnectsList.find(
        (item) => item.cloudVnConnectAct.wnumber === wnumber
      );
      if (!entity) return;
      await this.showConnectionDetails(entity);
    },
    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<CloudVnKcpsList>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});
</script>
